<script setup>
// assets/project/src/views/Competition/CompettitionExtraFields.vue
import { ref, onMounted } from 'vue'
import { PlusOutlined } from '@ant-design/icons-vue'
import { Button, Select, Input, Switch, message, Form } from 'ant-design-vue'
import { cloneDeep } from 'lodash'
import axios from 'axios'

// Типы доступных полей
const fieldTypes = [
	{ label: 'Текстовое поле', value: 'text' },
	{ label: 'Текстовая область', value: 'textarea' },
	{ label: 'Дата', value: 'date' },
	{ label: 'Радио кнопки', value: 'radio' },
	{ label: 'Чекбокс', value: 'checkbox' },
	{ label: 'Визуальный редактор', value: 'tinymce' },
	{ label: 'Информационное поле', value: 'info' },
	{ label: 'Загрузка файла', value: 'file' }
]

// Состояние полей формы
const formFields = ref([])
const competition = ref(null)
const isLoading = ref(false)
const fileTypes = [
	{ label: 'PDF', value: 'pdf' }
]

// Загрузка конкурса и его полей
async function fetchCompetition() {
	try {
		isLoading.value = true
		const response = await axios.get(`/api/competitions/${window.competitionId}`)
		competition.value = response.data
		
		// Инициализация полей из competitionData
		if (competition.value.competitionData?.extra_fields) {
			formFields.value = competition.value.competitionData.extra_fields
		}
	} catch (error) {
		message.error('Ошибка при загрузке конкурса')
		console.error(error)
	} finally {
		isLoading.value = false
	}
}

// Сохранение конкурса
async function saveCompetition() {
	try {
		isLoading.value = true
		const competitionData = {
			...competition.value.competition_data,
			extra_fields: formFields.value
		}
		
		await axios.patch(`/api/competitions/${window.competitionId}`, {
			competition_data: competitionData
		}, {
			headers: {
				'Content-Type': 'application/merge-patch+json'
			}
		})
		
		message.success('Данные успешно сохранены')
	} catch (error) {
		message.error('Ошибка при сохранении')
		console.error(error)
	} finally {
		isLoading.value = false
	}
}

onMounted(() => {
	fetchCompetition()
})

// Состояние редактирования
const isEditing = ref(false)
const editingIndex = ref(null)

// Форма для редактирования поля
const fieldForm = ref({
	label: '',
	type: 'text',
	required: false,
	key: '',
	options: []
})

// Ошибки валидации
const validationErrors = ref({
	key: '',
	label: ''
})

// Сброс формы
function resetFieldForm() {
	fieldForm.value = {
		label: '',
		type: 'text',
		required: false,
		key: '',
		options: [],
		info: '',
		fileType: 'pdf'
	}
	validationErrors.value = {
		key: '',
		label: ''
	}
	isEditing.value = false
	editingIndex.value = null
}

// Начало редактирования поля
function startEditing(field, index) {
	isEditing.value = true
	editingIndex.value = index
	fieldForm.value = cloneDeep(field)
	
	// Инициализация свойства userInput для опций
	if (field.type === 'radio' && Array.isArray(field.options)) {
		fieldForm.value.options = fieldForm.value.options.map(option => {
			return {
				...option,
				userInput: option.value === '_user_input_',
				value: option.value === '_user_input_' ? '' : option.value
			}
		})
	}
}

// Валидация ключа поля
function validateKey(key) {
	if (!key) {
		return 'Ключ поля обязателен'
	}
	if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(key)) {
		return 'Ключ может содержать только латинские буквы, цифры и знак подчеркивания. Должен начинаться с буквы или знака подчеркивания'
	}
	
	// Проверка на префикс только для новых полей
	if (!isEditing.value) {
		const prefix = competition.value && competition.value.id ? `comp${competition.value.id}_` : ''
		if (prefix && !key.startsWith(prefix)) {
			return `Ключ должен начинаться с префикса "${prefix}"`
		}
	}
	
	const existingField = formFields.value.find(
		(field, index) => field.key === key && index !== editingIndex.value
	)
	if (existingField) {
		return 'Поле с таким ключом уже существует'
	}
	return ''
}

// Сохранение поля
async function handleFieldSave() {
	// Сброс ошибок
	validationErrors.value = {
		key: '',
		label: ''
	}
	
	// Валидация
	let hasErrors = false
	
	// Проверяем label только если поле не info
	if (fieldForm.value.type !== 'info' && !fieldForm.value.label) {
		validationErrors.value.label = 'Название поля обязательно'
		hasErrors = true
	}
	
	const keyError = validateKey(fieldForm.value.key)
	if (keyError) {
		validationErrors.value.key = keyError
		hasErrors = true
	}
	
	if (hasErrors) {
		return
	}
	
	const field = { ...fieldForm.value }
	
	if (field.type === 'radio' && Array.isArray(field.options)) {
		field.options = field.options.map(option => {
			// Если опция с пользовательским вводом, устанавливаем специальное значение
			if (option.userInput) {
				return {
					label: option.label,
					value: '_user_input_',
					userInput: true
				}
			}
			return option
		})
	}
	
	// Для info типа очищаем ненужные поля
	if (field.type === 'info') {
		field.label = ''
		field.required = false
	}
	
	if (isEditing.value && editingIndex.value !== null) {
		// Обновляем существующее поле
		formFields.value[editingIndex.value] = field
		message.success('Поле обновлено')
	} else {
		// Добавляем новое поле
		formFields.value.push(field)
		message.success('Поле добавлено')
	}
	
	// Сохраняем изменения в API
	await saveCompetition()
	resetFieldForm()
}

// Отмена редактирования
function handleCancel() {
	resetFieldForm()
}

const transliterationMap = {
	'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd',
	'е': 'e', 'ё': 'e', 'ж': 'zh', 'з': 'z', 'и': 'i',
	'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm', 'н': 'n',
	'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't',
	'у': 'u', 'ф': 'f', 'х': 'h', 'ц': 'ts', 'ч': 'ch',
	'ш': 'sh', 'щ': 'sch', 'ъ': '', 'ы': 'y', 'ь': '',
	'э': 'e', 'ю': 'yu', 'я': 'ya'
}

function processKey(key) {
	if (!key) return ''
	
	// Проверяем, есть ли уже префикс
	const prefix = competition.value && competition.value.id ? `comp${competition.value.id}_` : ''
	
	// Если ключ уже начинается с префикса, удаляем его для обработки
	let baseKey = key
	if (prefix && key.startsWith(prefix)) {
		baseKey = key.substring(prefix.length)
	}
	
	// Приводим к нижнему регистру
	let processedKey = baseKey.toLowerCase()
	
	// Заменяем пробелы на подчеркивания
	processedKey = processedKey.replace(/\s+/g, '_')
	
	// Транслитерация
	processedKey = processedKey.split('').map(char => {
		return transliterationMap[char] || char
	}).join('')
	
	// Оставляем только разрешенные символы
	processedKey = processedKey.replace(/[^a-z0-9_]/g, '')
	
	// Добавляем префикс с ID конкурса для новых полей
	if (!isEditing.value && prefix) {
		return `${prefix}${processedKey}`
	}
	
	// Для редактирования возвращаем ключ как есть
	return isEditing.value ? key : processedKey
}

function handleKeyChange(e) {
	fieldForm.value.key = processKey(e.target.value)
}

// Удаление поля
async function handleFieldDelete(index) {
	formFields.value.splice(index, 1)
	await saveCompetition()
	message.success('Поле удалено')
}

// Перемещение поля
async function moveField(index, direction) {
	const newIndex = index + direction
	if (newIndex >= 0 && newIndex < formFields.value.length) {
		const fields = [...formFields.value]
		;[fields[index], fields[newIndex]] = [fields[newIndex], fields[index]]
		formFields.value = fields
		await saveCompetition()
	}
}

// Добавление/удаление опций для radio кнопок
function handleOptionAdd() {
	fieldForm.value.options.push({ label: '', value: '', userInput: false })
}

function handleOptionRemove(index) {
	fieldForm.value.options.splice(index, 1)
}
</script>

<template>
	<div class="form-builder">
		<!-- Форма добавления/редактирования поля -->
		<div class="field-form">
			<h3>{{ isEditing ? 'Редактирование поля' : 'Добавление нового поля' }}</h3>
			<Form layout="vertical" :disabled="isLoading">
				
				<Form.Item
					label="Название поля"
					v-if="fieldForm.type !== 'info'"
					required
					:validate-status="validationErrors.label ? 'error' : ''"
					:help="validationErrors.label"
				>
					<Input v-model:value="fieldForm.label" placeholder="например: Имя" />
				</Form.Item>
				
				<Form.Item
					label="Ключ поля (key)"
					required
					:validate-status="validationErrors.key ? 'error' : ''"
					:help="validationErrors.key"
				>
					<Input
						v-model:value="fieldForm.key"
						@change="handleKeyChange"
						:disabled="isEditing"
						placeholder="например: first_name"
					/>
				</Form.Item>
				
				
				
				<Form.Item label="Тип поля" required>
					<Select v-model:value="fieldForm.type">
						<Select.Option
							v-for="type in fieldTypes"
							:key="type.value"
							:value="type.value"
						>
							{{ type.label }}
						</Select.Option>
					</Select>
				</Form.Item>
				
				<Form.Item v-if="fieldForm.type !== 'info'" label="Обязательное поле">
					<Switch v-model:checked="fieldForm.required" />
				</Form.Item>
				
				<template v-if="fieldForm.type === 'file'">
					<Form.Item label="Тип файла" required>
						<Select v-model:value="fieldForm.fileType">
							<Select.Option
								v-for="type in fileTypes"
								:key="type.value"
								:value="type.value"
							>
								{{ type.label }}
							</Select.Option>
						</Select>
					</Form.Item>
				</template>
				
				<!-- Опции для radio кнопок -->
				<template v-if="fieldForm.type === 'radio'">
					<div v-for="(option, index) in fieldForm.options" :key="index" class="option-item">
						<Form.Item :label="`Опция ${index + 1}`">
							<div class="option-inputs">
								<Input v-model:value="option.label" placeholder="Текст" />
								<Input
									v-if="!option.userInput"
									v-model:value="option.value"
									placeholder="Значение"
								/>
								<div class="user-input-switch">
									<Switch v-model:checked="option.userInput" />
									<span>Значение задает пользователь</span>
								</div>
								<Button type="link" danger @click="handleOptionRemove(index)">
									Удалить
								</Button>
							</div>
						</Form.Item>
					</div>
					<Button type="dashed" @click="handleOptionAdd" class="add-option-btn">
						<PlusOutlined /> Добавить опцию
					</Button>
				</template>
				
				<template v-if="fieldForm.type === 'info'">
					<Form.Item label="Информационный текст" required>
						<Input.TextArea
							v-model:value="fieldForm.info"
							:rows="6"
							placeholder="Введите HTML текст"
						/>
					</Form.Item>
				</template>
				
				<div class="form-buttons">
					<Button type="primary" @click="handleFieldSave" :loading="isLoading">
						{{ isEditing ? 'Сохранить' : 'Добавить' }}
					</Button>
					<Button v-if="isEditing" @click="handleCancel">Отмена</Button>
				</div>
			</Form>
		</div>
		
		<!-- Список полей -->
		<div class="fields-list">
			<h3>Список полей</h3>
			<div v-if="isLoading" class="loading">Загрузка...</div>
			<div v-else>
				<div v-for="(field, index) in formFields" :key="field.key" class="field-item">
					<div class="field-info">
						<h4 v-if="field.type !== 'info'">{{ field.label }}</h4>
						<p>Ключ: {{ field.key }}</p>
						<p>Тип: {{ fieldTypes.find(t => t.value === field.type)?.label }}</p>
						<p v-if="field.type !== 'info'">Обязательное: {{ field.required ? 'Да' : 'Нет' }}</p>
						<div v-if="field.type === 'file'" class="file-info">
							<p>Тип файла: {{ field.fileType ? field.fileType.toUpperCase() : 'PDF' }}</p>
						</div>
						<div v-if="field.type === 'info'" class="info-preview">
							<p>Информация:</p>
							<div v-html="field.info"></div>
						</div>
					</div>
					<div class="field-actions">
						<Button @click="moveField(index, -1)" :disabled="index === 0">
							Вверх
						</Button>
						<Button @click="moveField(index, 1)" :disabled="index === formFields.length - 1">
							Вниз
						</Button>
						<Button type="primary" @click="startEditing(field, index)">
							Редактировать
						</Button>
						<Button type="primary" danger @click="handleFieldDelete(index)">
							Удалить
						</Button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.form-builder {
	padding: 20px;
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 20px;
}

.field-form {
	padding: 20px;
	border: 1px solid #eee;
	border-radius: 4px;
}

.fields-list {
	padding: 20px;
	border: 1px solid #eee;
	border-radius: 4px;
}

.field-item {
	border: 1px solid #eee;
	padding: 15px;
	margin-bottom: 10px;
	border-radius: 4px;
}

.field-info h4 {
	margin: 0 0 5px;
}

.field-info p {
	margin: 0;
	color: #666;
}

.field-actions {
	margin-top: 10px;
	display: flex;
	gap: 10px;
}

.form-buttons {
	display: flex;
	gap: 10px;
	margin-top: 20px;
}

.option-item {
	border: 1px dashed #ddd;
	padding: 10px;
	margin-bottom: 10px;
	border-radius: 4px;
}

.option-inputs {
	display: grid;
	gap: 10px;
	align-items: start;
}

.add-option-btn {
	width: 100%;
}

.loading {
	text-align: center;
	padding: 20px;
	color: #666;
}
.info-preview {
	margin-top: 10px;
	padding: 10px;
	background-color: #fafafa;
	border: 1px solid #eee;
	border-radius: 4px;
	max-height: 150px;
	overflow-y: auto;
}

.info-preview p {
	font-weight: bold;
	margin-bottom: 5px;
}
.user-input-switch {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 8px;
}

.user-input-switch span {
	font-size: 14px;
	color: #666;
}
.file-info {
	margin-top: 5px;
	padding: 5px 0;
}

.file-info p {
	margin: 0;
	color: #666;
}
</style>